import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { BlockCard } from "./block-card";
import { TemplateCard } from "./template-card";
import { useBlocks, useCategories } from "./hooks";

/** The gallery. Search and category filter both run server-side, so the URL of
 *  the request is the whole state — there is no client-side filtering to drift
 *  out of sync with what the API would return. */
export function BlocksPage() {
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");

  const { data: blocks, isPending, isError, error } = useBlocks(category, search);
  const { data: categories = [] } = useCategories();

  return (
    <div className="space-y-10">
      <header className="space-y-4 pt-4">
        <h1 className="max-w-2xl text-4xl font-semibold tracking-tight text-ink-50 sm:text-5xl">
          Backend blocks for the FastAPI template
        </h1>
        <p className="max-w-2xl text-lg leading-relaxed text-ink-400">
          Ports, adapters and their fakes — copied into your repo, not installed as a
          dependency. You own the code the moment it lands, so you can bend a port to fit
          instead of forking around a package.
        </p>
      </header>

      <TemplateCard />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blocks…"
            aria-label="Search blocks"
            className="w-full rounded-lg border border-ink-700 bg-ink-900 py-2.5 pl-9 pr-3 text-sm text-ink-50 outline-none transition-colors placeholder:text-ink-400 focus:border-ink-400"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={category === ""} onClick={() => setCategory("")}>
            all
          </FilterChip>
          {categories.map((name) => (
            <FilterChip
              key={name}
              active={category === name}
              onClick={() => setCategory(name)}
            >
              {name}
            </FilterChip>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 py-24 text-sm text-ink-400">
          <Loader2 className="animate-spin" size={16} />
          Loading blocks…
        </div>
      ) : isError ? (
        <p className="rounded-lg border border-ink-700 bg-ink-900 px-4 py-3 text-sm">
          Could not load the registry: {error.message}
        </p>
      ) : blocks.length === 0 ? (
        <p className="py-24 text-center text-sm text-ink-400">
          No blocks match that search.
        </p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {blocks.map((block) => (
            <BlockCard key={block.slug} block={block} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
        active
          ? "border-ink-400 bg-ink-800 text-ink-50"
          : "border-ink-700 text-ink-400 hover:border-ink-400 hover:text-ink-200"
      }`}
    >
      {children}
    </button>
  );
}
